import { useRecoverPasswordMutation } from "@common/api/users"
import { Button } from "@common/components/ui/button"
import { Input } from "@common/components/ui/input"
import { isLoggedIn } from "@common/hooks/use-auth"
import { emailPattern, handleError } from "@common/utils"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import useCustomToast from "../hooks/use-custom-toast"

interface FormData {
  email: string
}

export const Route = createFileRoute("/recover-password")({
  component: RecoverPassword,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function RecoverPassword() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()
  const showToast = useCustomToast()
  const mutation = useRecoverPasswordMutation()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    mutation.mutate(
      { email: data.email },
      {
        onSuccess: () => {
          showToast(
            "Email sent.",
            "If this email is registered, you will receive a password reset link.",
            "success",
          )
          reset()
        },
        onError: (err) => {
          handleError(err, showToast)
        },
      },
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-primary">Password Recovery</h1>
            <p className="text-muted-foreground">
              A password recovery email will be sent to the registered account.
            </p>
          </div>

          <div className="space-y-2">
            <Input
              placeholder="Email"
              type="email"
              {...register("email", {
                required: "Email is required",
                pattern: emailPattern,
              })}
            />
            {errors.email && (
              <p className="text-small-medium text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          <Button className="w-full" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sending…" : "Continue"}
          </Button>
        </form>
      </div>
    </div>
  )
}
